import React, { ReactNode } from 'react';

export type HeroBannerVariant = 'full-width' | 'panel';
export type HeroBannerContentPosition = 'left' | 'center' | 'right';
export type HeroBannerContentVerticalAlign = 'top' | 'middle' | 'bottom';
export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface HeroBannerImage {
  src: string;
  alt?: string;
  overlayOpacity?: number;
}

export interface HeroBannerProps {
  variant?: HeroBannerVariant;
  heading?: ReactNode;
  headingLevel?: HeadingLevel;
  subheading?: ReactNode;
  categoryLabel?: string;
  children?: ReactNode;
  cta?: ReactNode;
  backgroundImage?: HeroBannerImage;
  contentPosition?: HeroBannerContentPosition;
  contentWidthClass?: string;
  contentVerticalAlign?: HeroBannerContentVerticalAlign;
  backgroundColor?: string;
  textColor?: string;
  hasBorder?: boolean;
  withBottomMargin?: boolean;
  className?: string;
  dataAttributes?: Record<string, string>;
}

function HeroBanner({
  variant = 'full-width',
  heading,
  headingLevel = 'h2',
  subheading,
  categoryLabel,
  children,
  cta,
  backgroundImage,
  contentPosition = 'left',
  contentWidthClass = '',
  contentVerticalAlign = 'middle',
  backgroundColor = '#000',
  textColor,
  hasBorder = false,
  withBottomMargin = true,
  className = '',
  dataAttributes = {},
}: HeroBannerProps) {
  const actualTextColor =
    textColor || backgroundColor === '#fff' || backgroundColor === '#ffffff'
      ? '#000'
      : '#fff';

  const dataProps: Record<string, string> = {};
  Object.entries(dataAttributes).forEach(([key, value]) => {
    dataProps[`data-${key}`] = value;
  });

  const contentPositionClass = {
    left: 'au-text-left au-items-start',
    center: 'au-text-center au-items-center',
    right: 'au-text-right au-items-end',
  }[contentPosition];

  const verticalAlignClass = {
    top: 'au-justify-start',
    middle: 'au-justify-center',
    bottom: 'au-justify-end',
  }[contentVerticalAlign];

  const variantClasses = {
    'full-width': {
      container: 'au-w-full au-h-[70vh] au-min-h-[400px]',
      content: 'au-z-10 au-px-16 md:au-px-12 au-py-12',
      overlay: 'au-bg-black au-bg-opacity-50',
    },
    panel: {
      container: 'au-mx-auto au-rounded-lg au-overflow-hidden au-min-h-[400px]',
      content: 'au-z-10 au-py-10 md:au-py-16 au-max-w-[70%] au-ml-[5%]',
      overlay: 'au-bg-black au-bg-opacity-40',
    },
  }[variant];

  const getHeadingClass = (level: HeadingLevel) => {
    const baseClass = 'au-font-bold au-mb-3 md:au-mb-4';

    switch (level) {
      case 'h1':
        return `${baseClass} au-text-3xl md:au-text-4xl lg:au-text-[40px]`;
      case 'h2':
        return `${baseClass} au-text-2xl md:au-text-3xl lg:au-text-[34px]`;
      case 'h3':
        return `${baseClass} au-text-xl md:au-text-2xl lg:au-text-3xl`;
      case 'h4':
        return `${baseClass} au-text-lg md:au-text-xl lg:au-text-2xl`;
      case 'h5':
        return `${baseClass} au-text-base md:au-text-lg lg:au-text-xl`;
      case 'h6':
        return `${baseClass} au-text-sm md:au-text-base lg:au-text-lg`;
      default:
        return `${baseClass} au-text-2xl md:au-text-3xl lg:au-text-4xl`;
    }
  };

  const HeadingTag = headingLevel as keyof JSX.IntrinsicElements;

  return (
    <div
      className={`
        au-relative au-flex
        ${variantClasses.container}
        ${
          hasBorder
            ? 'au-border au-border-solid au-border-black au-rounded-lg au-overflow-hidden'
            : ''
        }
        ${withBottomMargin ? 'au-mb-8 md:au-mb-16' : ''}
        ${className}
      `}
      style={{ backgroundColor, color: actualTextColor }}
      {...dataProps}
    >
      <div
        className={`
        au-container au-flex au-flex-col au-relative au-gap-2 md:au-px-16
        ${contentWidthClass}
        ${contentPositionClass}
        ${verticalAlignClass}
        ${variantClasses.content}
      `}
      >
        {categoryLabel && (
          <div className="au-text-md au-mb-2 md:au-mb-3">{categoryLabel}</div>
        )}

        {heading && (
          <HeadingTag className={getHeadingClass(headingLevel)}>
            {heading}
          </HeadingTag>
        )}

        {subheading && (
          <div
            className={`${
              variant === 'panel' ? 'au-text-[16px]' : 'au-text-[18px]'
            }  md:au-mb-6`}
          >
            {subheading}
          </div>
        )}

        {children && <div className="au-mb-6">{children}</div>}

        {cta && <div className="au-flex au-flex-wrap au-gap-4">{cta}</div>}
      </div>

      {backgroundImage && (
        <div
          className={`
          au-absolute au-inset-0 au-w-full au-h-full au-z-0
          ${variantClasses.overlay}
        `}
        >
          <img
            src={backgroundImage.src}
            alt={backgroundImage.alt || ''}
            className="au-w-full au-h-full au-object-cover"
            loading="eager"
          />

          {backgroundImage.overlayOpacity !== undefined && (
            <div
              className="au-absolute au-inset-0 au-z-[1] au-bg-black"
              style={{ opacity: backgroundImage.overlayOpacity / 100 }}
            />
          )}
        </div>
      )}
    </div>
  );
}

HeroBanner.attributes = {
  variant: {
    type: 'string',
    options: ['full-width', 'panel'],
  },
  heading: {
    type: 'string',
  },
  headingLevel: {
    type: 'string',
    options: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  },
  subheading: {
    type: 'string',
  },
  categoryLabel: {
    type: 'string',
  },
  backgroundImage: {
    type: 'object',
  },
  contentPosition: {
    type: 'string',
    options: ['left', 'center', 'right'],
  },
  contentVerticalAlign: {
    type: 'string',
    options: ['top', 'middle', 'bottom'],
  },
  backgroundColor: {
    type: 'string',
  },
  textColor: {
    type: 'string',
  },
  hasBorder: {
    type: 'boolean',
  },
  withBottomMargin: {
    type: 'boolean',
  },
  className: {
    type: 'string',
  },
  dataAttributes: {
    type: 'object',
  },
  ctaText: {
    type: 'string',
  },
  ctaVariant: {
    type: 'string',
    options: ['outlined', 'contained', 'text'],
  },
  ctaHref: {
    type: 'string',
  },
};

export default HeroBanner;
